import type { LocationQuery, LocationQueryValue } from 'vue-router'
import type { Column } from '@/types'
import { AFP_REGIONS } from 'afpnews-api'

export function convertQueryParamToSingle(
  param: LocationQueryValue | LocationQueryValue[] | undefined,
  defaultValue?: string
): string | undefined {
  let value: string | undefined
  if (Array.isArray(param)) {
    value = typeof param[0] === 'string' ? param[0] : undefined
  } else if (typeof param === 'string') {
    value = param
  }

  value = value?.trim()
  return value || defaultValue
}

export function convertQueryParamToMultiple(
  param: LocationQueryValue | LocationQueryValue[] | undefined,
  defaultValue?: string,
  splitCharacter: string = ','
): string[] {
  let values: string[] = []
  if (param === undefined) {
    values = defaultValue ? [defaultValue] : []
  } else if (Array.isArray(param)) {
    values = param.filter((p): p is string => typeof p === 'string')
  } else if (typeof param === 'string') {
    values = param.split(splitCharacter).map(s => s.trim())
  }
  values = values.map(p => p.trim()).filter(p => p.length > 0)
  return values.length > 0 ? values : defaultValue ? [defaultValue] : []
}

export function wrapDoubleQuotes(str: string) {
  if (str.includes(' ') || str.includes(':')) {
    return `"${str}"`
  }
  return str
}

export type DeckColumnInput = Pick<Column, 'params'> &
  Partial<Pick<Column, 'type'>>

export function buildEventQuery(ids: string[]) {
  return ids
    .map(
      e =>
        `event:${wrapDoubleQuotes(e.startsWith('afpevent:') ? e : `afpevent:${e}`)}`
    )
    .join(' OR ')
}

export function buildColumnFromDeckQuery(
  query: LocationQuery,
  defaultLocale: string
): DeckColumnInput {
  const {
    lang,
    q,
    slug,
    slugs,
    topic,
    country,
    city,
    genre,
    event,
    creator,
    afpshortid,
    class: classParam,
    region,
    from,
    to
  } = query
  const slugValue = slug ?? slugs

  const column: DeckColumnInput = {
    params: {
      // lang=all : toutes les langues (sans lang, on retombe sur la langue par défaut)
      langs:
        convertQueryParamToSingle(lang) === 'all'
          ? []
          : convertQueryParamToMultiple(lang, defaultLocale),
      query: ''
    }
  }

  const dateFrom = convertQueryParamToSingle(from)
  const dateTo = convertQueryParamToSingle(to)
  if (dateFrom) column.params.dateFrom = dateFrom
  if (dateTo) column.params.dateTo = dateTo

  if (topic !== undefined) {
    column.type = 'topic'
    column.params.topic = convertQueryParamToSingle(topic)
  }
  if (classParam !== undefined) {
    const cls = convertQueryParamToSingle(classParam)
    column.params.class = cls
    if (cls === 'picture') column.params.langs = ['en']
  }

  const segments: string[] = []
  if (q !== undefined) {
    segments.push(convertQueryParamToSingle(q)!)
  }
  if (slugValue !== undefined) {
    segments.push(
      convertQueryParamToMultiple(slugValue)
        .map(s => `slug:${wrapDoubleQuotes(s)}`)
        .join(' AND ')
    )
  }
  if (country !== undefined) {
    segments.push(
      `country:${wrapDoubleQuotes(convertQueryParamToSingle(country)!)}`
    )
  }
  // Régions inconnues ignorées : afpnews-api lèverait une erreur et la colonne resterait vide
  const regions = convertQueryParamToMultiple(region).filter(r =>
    Object.hasOwn(AFP_REGIONS, r)
  )
  if (regions.length) {
    segments.push(regions.map(r => `region:${r}`).join(' OR '))
  }
  if (city !== undefined) {
    segments.push(`city:${wrapDoubleQuotes(convertQueryParamToSingle(city)!)}`)
  }
  if (genre !== undefined) {
    segments.push(
      convertQueryParamToMultiple(genre)
        .map(g => `genre:${wrapDoubleQuotes(g)}`)
        .join(' OR ')
    )
  }
  if (event !== undefined) {
    segments.push(buildEventQuery(convertQueryParamToMultiple(event)))
    if (column.type === undefined) column.type = 'event'
  }
  if (creator !== undefined) {
    segments.push(
      `creator:${wrapDoubleQuotes(convertQueryParamToSingle(creator)!)}`
    )
  }
  if (afpshortid !== undefined) {
    segments.push(
      convertQueryParamToMultiple(afpshortid)
        .map(s => `afpshortid:${wrapDoubleQuotes(s.toUpperCase())}`)
        .join(' OR ')
    )
    if (lang === undefined) column.params.langs = []
  }

  column.params.query = segments
    .map(s => (segments.length > 1 ? `(${s})` : s))
    .join(' AND ')
  return column
}

// Clés lues par buildColumnFromDeckQuery
export const DECK_QUERY_KEYS = [
  'lang',
  'q',
  'slug',
  'slugs',
  'topic',
  'country',
  'city',
  'genre',
  'event',
  'creator',
  'afpshortid',
  'class',
  'region',
  'from',
  'to'
]
